import { useDisclosure } from '@chakra-ui/react';
import { startOfMonth } from 'date-fns';
import { useState } from 'react';

import { DateValue } from '../types';

type MonthPickerOptions = {
	disableMonthIf?: (month: Date) => boolean;
	onChange: (value: DateValue) => void;
	value: DateValue;
};

export const useMonthPicker = ({ disableMonthIf, onChange, value }: MonthPickerOptions) => {
	const popover = useDisclosure();

	// Start from the current year, or the year of the selected month
	const initialYear = value?.getFullYear() || new Date().getFullYear();
	const [currentYear, setCurrentYear] = useState(initialYear);

	const onOpen = () => {
		popover.onOpen();
		// Reset to the year of the selected month, or current year
		setCurrentYear(value?.getFullYear() || new Date().getFullYear());
	};

	const onSelectMonth = (month: DateValue) => {
		if (month) {
			const monthStart = startOfMonth(month);
			onChange(monthStart);
		} else {
			onChange(null);
		}
		popover.onClose();
	};

	const onPrevYear = () => {
		setCurrentYear((prev) => prev - 1);
	};

	const onNextYear = () => {
		const currentDate = new Date();
		// If disableMonthIf function exists, check if next year's January would be disabled
		// If so, don't allow navigation beyond current year + 10
		const nextYear = currentYear + 1;
		const januaryNextYear = new Date(nextYear, 0, 1);
		const maxYear = disableMonthIf?.(januaryNextYear) ? currentDate.getFullYear() : currentDate.getFullYear() + 10;

		setCurrentYear((prev) => Math.min(prev + 1, maxYear));
	};

	return {
		currentYear,
		disableMonthIf,
		interimValue: value,
		isOpen: popover.isOpen,
		onClear: () => onChange(null),
		onClose: popover.onClose,
		onHoverMonth: () => void 0, // No-op for single month picker
		onNextYear,
		onOpen,
		onPrevYear,
		onSelectMonth,
		onClearHover: () => void 0, // No-op for single month picker
		selectionEnd: value,
		selectionStart: value,
	};
};
